import { API_CONFIG } from '../config/apiConfig';
import { safeFetch } from './extensionErrorHandler';
import { getDeviceId } from './deviceId';
import { logDebug } from './logger';

const LIKES_ENABLED = ((process.env.REACT_APP_LIKES_ENABLED || (process.env.NODE_ENV === 'production' ? 'true' : 'false')).toString().toLowerCase() === 'true');

// Cache configuration
const CACHE_DURATION = 300000; // 5 minute cache (increased from 1 minute)
const BATCH_DELAY = 1000; // 1 second delay before executing batched requests (increased from 500ms)

// Cache storage
const cache = {
  status: new Map(), // projectId -> { data, timestamp }
  count: new Map(),  // projectId -> { data, timestamp }
};

// Request queues for batching
const requestQueues = {
  status: new Map(), // projectId -> Promise
  count: new Map(),  // projectId -> Promise
};

// Rate limiting
let requestCount = 0;
let lastResetTime = Date.now();
const RATE_LIMIT_WINDOW = 60000; // 1 minute
const RATE_LIMIT_MAX = 20; // Max 20 requests per minute (reduced from 50 to be more conservative)

// Check if we're hitting rate limits
const checkRateLimit = () => {
  const now = Date.now();
  if (now - lastResetTime > RATE_LIMIT_WINDOW) {
    requestCount = 0;
    lastResetTime = now;
  }
  
  if (requestCount >= RATE_LIMIT_MAX) {
    logDebug('Rate limit reached, delaying requests');
    return false;
  }
  
  requestCount++;
  return true;
};

// Check if cached data is still valid
const isCacheValid = (cacheEntry) => {
  if (!cacheEntry) return false;
  return Date.now() - cacheEntry.timestamp < CACHE_DURATION;
};

// Get cached data or return null
const getCachedData = (type, projectId) => {
  const cacheEntry = cache[type].get(projectId);
  return isCacheValid(cacheEntry) ? cacheEntry.data : null;
};

// Set cached data
const setCachedData = (type, projectId, data) => {
  cache[type].set(projectId, {
    data,
    timestamp: Date.now()
  });
};

// Batch multiple requests with delay and concurrency control
const batchRequest = async (type, projectId, requestFn) => {
  // Check if request is already in progress
  if (requestQueues[type].has(projectId)) {
    return requestQueues[type].get(projectId);
  }

  // Create batched request promise
  const requestPromise = new Promise((resolve) => {
    setTimeout(async () => {
      try {
        // Check rate limit before making request
        if (!checkRateLimit()) {
          // If rate limited, return cached data or default
          const cached = getCachedData(type, projectId);
          if (cached !== null) {
            resolve(cached);
            return;
          }
          // Return default values if no cache and rate limited
          resolve(type === 'status' ? { liked: false } : 0);
          return;
        }

        const result = await requestFn();
        setCachedData(type, projectId, result);
        resolve(result);
      } catch (error) {
        logDebug(`Batched ${type} request failed for ${projectId}:`, error.message);
        // Return cached data on error, or defaults
        const cached = getCachedData(type, projectId);
        resolve(cached !== null ? cached : (type === 'status' ? { liked: false } : 0));
      } finally {
        // Remove from queue
        requestQueues[type].delete(projectId);
      }
    }, BATCH_DELAY);
  });

  // Store promise in queue
  requestQueues[type].set(projectId, requestPromise);
  return requestPromise;
};

// Optimized like status fetcher with caching and batching
export const getLikeStatus = async (projectId) => {
  if (!LIKES_ENABLED) return { liked: false };

  // Check cache first
  const cached = getCachedData('status', projectId);
  if (cached !== null) {
    return cached;
  }

  // Use batched request
  return batchRequest('status', projectId, async () => {
    const deviceId = getDeviceId();
    const url = `${API_CONFIG.ENDPOINTS.LIKES_STATUS}?projectId=${encodeURIComponent(projectId)}&deviceId=${encodeURIComponent(deviceId)}`;
    
    const res = await safeFetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  });
};

// Optimized like count fetcher with caching and batching
export const getLikeCount = async (projectId) => {
  if (!LIKES_ENABLED) return 0;

  // Check cache first
  const cached = getCachedData('count', projectId);
  if (cached !== null) {
    return cached;
  }

  // Use batched request
  return batchRequest('count', projectId, async () => {
    const url = `${API_CONFIG.ENDPOINTS.LIKES_COUNT}?projectId=${encodeURIComponent(projectId)}`;
    
    const res = await safeFetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.count || 0;
  });
};

// Batch fetch multiple project counts at once - TRUE BATCH API
export const getBatchLikeCounts = async (projectIds) => {
  if (!LIKES_ENABLED) {
    return projectIds.reduce((acc, id) => ({ ...acc, [id]: 0 }), {});
  }

  const results = {};
  const uncachedProjects = [];

  // Check cache for all projects first
  for (const projectId of projectIds) {
    const cached = getCachedData('count', projectId);
    if (cached !== null) {
      results[projectId] = cached;
    } else {
      uncachedProjects.push(projectId);
    }
  }

  // If all projects are cached, return immediately
  if (uncachedProjects.length === 0) {
    return results;
  }

  // Check rate limit before making batch request
  if (!checkRateLimit()) {
    logDebug('Rate limit reached, returning cached/default values');
    // Return default values for uncached projects
    for (const projectId of uncachedProjects) {
      results[projectId] = 0;
    }
    return results;
  }

  try {
    // Make a single batch request for all uncached projects
    logDebug(`Making batch request for ${uncachedProjects.length} projects:`, uncachedProjects);
    
    // Use the individual count endpoint but with better error handling and delays
    const batchPromises = uncachedProjects.map(async (projectId, index) => {
      // Add staggered delays to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, index * 200)); // 200ms between each request
      
      try {
        const url = `${API_CONFIG.ENDPOINTS.LIKES_COUNT}?projectId=${encodeURIComponent(projectId)}`;
        const res = await safeFetch(url);
        
        if (!res.ok) {
          if (res.status === 429) {
            logDebug(`Rate limited for project ${projectId}, using default`);
            return { projectId, count: 0 };
          }
          throw new Error(`HTTP ${res.status}`);
        }
        
        const data = await res.json();
        const count = data.count || 0;
        
        // Cache the result
        setCachedData('count', projectId, count);
        
        return { projectId, count };
      } catch (error) {
        logDebug(`Failed to fetch count for ${projectId}:`, error.message);
        return { projectId, count: 0 };
      }
    });

    // Execute all requests with a reasonable timeout
    const batchResults = await Promise.allSettled(batchPromises);
    
    // Process results
    batchResults.forEach((result, index) => {
      const projectId = uncachedProjects[index];
      if (result.status === 'fulfilled' && result.value) {
        results[result.value.projectId] = result.value.count;
      } else {
        results[projectId] = 0;
      }
    });

  } catch (error) {
    logDebug('Batch like counts request failed:', error.message);
    // Return default values for all uncached projects
    for (const projectId of uncachedProjects) {
      results[projectId] = 0;
    }
  }

  return results;
};

// Toggle like with cache invalidation
export const toggleLike = async (projectId, action) => {
  if (!LIKES_ENABLED) return { liked: action === 'like', count: undefined };

  try {
    // Check rate limit
    if (!checkRateLimit()) {
      throw new Error('Rate limit exceeded');
    }

    const deviceId = getDeviceId();
    const res = await safeFetch(API_CONFIG.ENDPOINTS.LIKES_TOGGLE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId, deviceId, action })
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    // Update cache with new data
    setCachedData('status', projectId, { liked: data.liked });
    setCachedData('count', projectId, data.count);

    return data;
  } catch (error) {
    logDebug('Like toggle failed, using fallback:', error.message);
    
    // Fallback: update cache optimistically and send to tracking endpoint
    const currentCount = getCachedData('count', projectId) || 0;
    
    const newLiked = action === 'like';
    const newCount = Math.max(0, currentCount + (newLiked ? 1 : -1));
    
    setCachedData('status', projectId, { liked: newLiked });
    setCachedData('count', projectId, newCount);

    // Send to tracking endpoint as fallback
    try {
      await safeFetch(API_CONFIG.ENDPOINTS.TRACK_VISIT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section: `portfolio-item-${action}`,
          duration: 1,
          sessionId: `${Date.now()}-${Math.random()}`,
          pageUrl: window.location.href,
          projectId,
          deviceId: getDeviceId(),
          interactionType: action
        })
      });
    } catch (trackingError) {
      logDebug('Fallback tracking also failed:', trackingError.message);
    }

    return { liked: newLiked, count: newCount };
  }
};

// Clear cache (useful for testing or manual refresh)
export const clearLikesCache = () => {
  cache.status.clear();
  cache.count.clear();
  requestQueues.status.clear();
  requestQueues.count.clear();
  requestCount = 0;
  lastResetTime = Date.now();
  logDebug('Likes cache cleared');
};

// Get cache statistics (for debugging)
export const getCacheStats = () => {
  return {
    statusCacheSize: cache.status.size,
    countCacheSize: cache.count.size,
    activeStatusRequests: requestQueues.status.size,
    activeCountRequests: requestQueues.count.size,
    requestCount,
    timeUntilReset: Math.max(0, RATE_LIMIT_WINDOW - (Date.now() - lastResetTime))
  };
};